import * as React from "react";
import ReactDOM from "react-dom";
import {
    Edit,
    FormWithRedirect,
    TextInput,
    Toolbar,
    SaveButton,
    ReferenceInput,
    SelectInput,
    DateInput,
    ResourceContextProvider,
    BooleanInput,
    NumberInput,
    List,
    DeleteButton,
    EditButton,
    TextField,
    Datagrid,
    useDataProvider,
    useRefresh,
    Button,
    Create,
    SimpleForm,
    FunctionField,
    useMutation,
    useRedirect,
    useNotify,
    required,
    email as validateEmail,
} from 'react-admin';

import { Typography, Box, Tabs, Tab, Divider, Table, TableHead, TableRow, TableCell, TableBody, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField as MuiTextField, Checkbox, FormControlLabel, Chip, Button as MuiButton, ThemeProvider, createTheme } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import RefreshIcon from '@material-ui/icons/Refresh';

import { EmploymentUpdateRequestList } from './employment-update-requests';
import { HiatusesPanel } from './settings';
import { AddEmploymentUsers, EmploymentUsersList } from "./employment-users";
import PenaltyGroupAssignmentPanel from './components/PenaltyGroupAssignmentPanel';

import GetAppIcon from "@material-ui/icons/GetApp";
import { downloadGetFileResult } from "../functions/downloadGetFileResult";
import ProductionEmploymentsImportForm from "./components/ProductionEmploymentsImportForm";
import { useDealGuard } from './components/DealGuard';
import { NoticePanel } from './components/NoticePanel';
import { NoticePeriodInput } from './components/NoticePeriodInput';
import { formatDate } from './components/noticeApi';

const ALLOWANCE_TYPES = [
    { value: 'box', label: 'Box' },
    { value: 'car', label: 'Car' },
    { value: 'computer', label: 'Computer' },
    { value: 'phone', label: 'Phone' },
    { value: 'living', label: 'Living' },
    { value: 'hire_car', label: 'Hire Car' },
    { value: 'first_aid', label: 'First Aid' },
    { value: 'equipment', label: 'Equipment' },
];

// ---------------------------------------------------------------------------
// Job Title Warning Modal
// ---------------------------------------------------------------------------



// ---------------------------------------------------------------------------
// Allowances Panel
// ---------------------------------------------------------------------------

const EmploymentAllowancesPanel = ({ employmentId, productionId }) => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const { run: runGuarded, guardDialog } = useDealGuard();
    const [allowances, setAllowances] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [editing, setEditing] = React.useState(null);
    const [form, setForm] = React.useState({
        type: 'box', daily_amount: '', prep: false, shoot: false, wrap: false, cap: '', description: ''
    });

    const load = () => {
        setLoading(true);
        dataProvider.getList(`employees/${employmentId}/allowances`, { pagination: {}, sort: {}, filter: {} })
            .then(({ data }) => { setAllowances(data); setLoading(false); })
            .catch(() => setLoading(false));
    };

    React.useEffect(() => { load(); }, [employmentId]);

    const openAdd = () => {
        setEditing(null);
        setForm({ type: 'box', daily_amount: '', prep: false, shoot: false, wrap: false, cap: '', description: '' });
        setDialogOpen(true);
    };

    const openEdit = (allowance) => {
        setEditing(allowance);
        setForm({
            type: allowance.type,
            daily_amount: allowance.daily_amount,
            prep: allowance.prep,
            shoot: allowance.shoot,
            wrap: allowance.wrap,
            cap: allowance.cap ?? '',
            description: allowance.description ?? '',
        });
        setDialogOpen(true);
    };

    const handleSave = () => {
        const payload = { ...form };
        if (!payload.cap) delete payload.cap;
        if (!payload.description) delete payload.description;

        runGuarded(
            (extra) => editing
                ? dataProvider.update(`employees/${employmentId}/allowances`, { id: editing.id, data: { ...payload, ...extra } })
                : dataProvider.create(`employees/${employmentId}/allowances`, { data: { ...payload, ...extra } }),
            {
                onSuccess: () => {
                    notify(editing ? 'Allowance updated' : 'Allowance added', 'success');
                    setDialogOpen(false);
                    load();
                },
                onError: err => notify(err?.body?.type?.[0] ?? 'Error saving allowance', 'error'),
            }
        );
    };

    const handleDelete = (id) => {
        runGuarded(
            (extra) => dataProvider.delete(`employees/${employmentId}/allowances`, { id, data: extra }),
            {
                onSuccess: () => { notify('Allowance deleted', 'success'); load(); },
                onError: () => notify('Error deleting allowance', 'error'),
            }
        );
    };

    return (
        <Box mb={3}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="h6">Allowances</Typography>
                <Button label="Add Allowance" onClick={openAdd}><AddIcon /></Button>
            </Box>
            {loading ? <Typography variant="body2">Loading...</Typography> : (
                allowances.length === 0
                    ? <Typography variant="body2" color="textSecondary">No allowances added.</Typography>
                    : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Daily</TableCell>
                                    <TableCell>Weekly</TableCell>
                                    <TableCell>Prep</TableCell>
                                    <TableCell>Shoot</TableCell>
                                    <TableCell>Wrap</TableCell>
                                    <TableCell>Cap</TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {allowances.map(a => (
                                    <TableRow key={a.id}>
                                        <TableCell>{ALLOWANCE_TYPES.find(t => t.value === a.type)?.label ?? a.type}</TableCell>
                                        <TableCell>£{a.daily_amount}</TableCell>
                                        <TableCell>£{a.weekly_amount ?? '—'}</TableCell>
                                        <TableCell>{a.prep ? '✓' : '—'}</TableCell>
                                        <TableCell>{a.shoot ? '✓' : '—'}</TableCell>
                                        <TableCell>{a.wrap ? '✓' : '—'}</TableCell>
                                        <TableCell>{a.cap ? `£${a.cap}` : '—'}</TableCell>
                                        <TableCell>
                                            <IconButton size="small" onClick={() => openEdit(a)}><EditIcon fontSize="small" /></IconButton>
                                            <IconButton size="small" onClick={() => handleDelete(a.id)}><DeleteIcon fontSize="small" /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )
            )}

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{editing ? 'Edit Allowance' : 'Add Allowance'}</DialogTitle>
                <DialogContent>
                    <Box mb={2}>
                        <MuiTextField
                            select
                            label="Type"
                            value={form.type}
                            onChange={e => setForm({ ...form, type: e.target.value })}
                            fullWidth
                            disabled={!!editing}
                            SelectProps={{ native: true }}
                        >
                            {ALLOWANCE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </MuiTextField>
                    </Box>
                    <Box mb={2}>
                        <MuiTextField
                            label="Daily Amount (£)"
                            type="number"
                            value={form.daily_amount}
                            onChange={e => setForm({ ...form, daily_amount: e.target.value })}
                            fullWidth
                        />
                    </Box>
                    <Box mb={2}>
                        <MuiTextField
                            label="Cap (£) — optional"
                            type="number"
                            value={form.cap}
                            onChange={e => setForm({ ...form, cap: e.target.value })}
                            fullWidth
                        />
                    </Box>
                    <Box mb={2}>
                        <MuiTextField
                            label="Description — optional"
                            value={form.description}
                            onChange={e => setForm({ ...form, description: e.target.value })}
                            fullWidth
                        />
                    </Box>
                    <Box display="flex">
                        <FormControlLabel control={<Checkbox checked={form.prep} onChange={e => setForm({ ...form, prep: e.target.checked })} />} label="Prep" />
                        <FormControlLabel control={<Checkbox checked={form.shoot} onChange={e => setForm({ ...form, shoot: e.target.checked })} />} label="Shoot" />
                        <FormControlLabel control={<Checkbox checked={form.wrap} onChange={e => setForm({ ...form, wrap: e.target.checked })} />} label="Wrap" />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button label="Cancel" onClick={() => setDialogOpen(false)} />
                    <Button label="Save" onClick={handleSave} />
                </DialogActions>
            </Dialog>

            {guardDialog}
        </Box>
    );
};

// ---------------------------------------------------------------------------
// Penalty Rates Panel
// ---------------------------------------------------------------------------

const PenaltyRatesPanel = ({ employmentId }) => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const { run: runGuarded, guardDialog } = useDealGuard();
    const [rates, setRates] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [editing, setEditing] = React.useState(null);
    const [rateValue, setRateValue] = React.useState('');

    const load = () => {
        setLoading(true);
        dataProvider.getList(`employees/${employmentId}/penalty-rates`, { pagination: {}, sort: {}, filter: {} })
            .then(({ data }) => { setRates(data); setLoading(false); })
            .catch(() => setLoading(false));
    };

    React.useEffect(() => { load(); }, [employmentId]);

    const openEdit = (rate) => {
        setEditing(rate);
        setRateValue(rate.rate);
        setDialogOpen(true);
    };

    const handleSave = () => {
        runGuarded(
            (extra) => dataProvider.update(`employees/${employmentId}/penalty-rates`, { id: editing.id, data: { rate: rateValue, ...extra } }),
            {
                onSuccess: () => {
                    notify('Rate updated', 'success');
                    setDialogOpen(false);
                    load();
                },
                onError: () => notify('Error updating rate', 'error'),
            }
        );
    };

    const handleReset = (rate) => {
        runGuarded(
            (extra) => dataProvider.create(`employees/${employmentId}/penalty-rates/${rate.id}/reset`, { data: { ...extra } }),
            {
                onSuccess: () => { notify('Rate reset to calculated value', 'success'); load(); },
                onError: () => notify('Error resetting rate', 'error'),
            }
        );
    };

    return (
        <Box mb={3}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="h6">Penalty Rates</Typography>
            </Box>
            {loading ? <Typography variant="body2">Loading...</Typography> : (
                rates.length === 0
                    ? <Typography variant="body2" color="textSecondary">No penalty rates calculated. Set an hourly rate and assign a penalty group to generate rates.</Typography>
                    : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Rate (£/hr)</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rates.map(r => (
                                    <TableRow key={r.id}>
                                        <TableCell>{r.penalty_type?.full_name ?? r.penalty_type_id}</TableCell>
                                        <TableCell>£{parseFloat(r.rate).toFixed(2)}</TableCell>
                                        <TableCell>
                                            {r.is_override
                                                ? <Chip label="Override" size="small" color="secondary" />
                                                : <Chip label="Calculated" size="small" />
                                            }
                                        </TableCell>
                                        <TableCell>
                                            <IconButton size="small" onClick={() => openEdit(r)} title="Override rate"><EditIcon fontSize="small" /></IconButton>
                                            {r.is_override && (
                                                <IconButton size="small" onClick={() => handleReset(r)} title="Reset to calculated"><RefreshIcon fontSize="small" /></IconButton>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )
            )}

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Override Penalty Rate — {editing?.penalty_type?.short_name}</DialogTitle>
                <DialogContent>
                    <Box mt={1}>
                        <MuiTextField
                            label="Rate (£/hr)"
                            type="number"
                            value={rateValue}
                            onChange={e => setRateValue(e.target.value)}
                            fullWidth
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button label="Cancel" onClick={() => setDialogOpen(false)} />
                    <Button label="Save" onClick={handleSave} />
                </DialogActions>
            </Dialog>

            {guardDialog}
        </Box>
    );
};

// ---------------------------------------------------------------------------
// Contract Notes Panel
// ---------------------------------------------------------------------------

const EmploymentContractNotesPanel = ({ employmentId }) => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const { run: runGuarded, guardDialog } = useDealGuard();
    const [notes, setNotes] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [editing, setEditing] = React.useState(null);
    const [noteText, setNoteText] = React.useState('');

    const load = () => {
        setLoading(true);
        dataProvider.getList(`employees/${employmentId}/contract-notes`, { pagination: {}, sort: {}, filter: {} })
            .then(({ data }) => { setNotes(data); setLoading(false); })
            .catch(() => setLoading(false));
    };

    React.useEffect(() => { load(); }, [employmentId]);

    const openAdd = () => {
        setEditing(null);
        setNoteText('');
        setDialogOpen(true);
    };

    const openEdit = (note) => {
        setEditing(note);
        setNoteText(note.note);
        setDialogOpen(true);
    };

    const handleSave = () => {
        runGuarded(
            (extra) => editing
                ? dataProvider.update(`employees/${employmentId}/contract-notes`, { id: editing.id, data: { note: noteText, ...extra } })
                : dataProvider.create(`employees/${employmentId}/contract-notes`, { data: { note: noteText, ...extra } }),
            {
                onSuccess: () => {
                    notify(editing ? 'Note updated' : 'Note added', 'success');
                    setDialogOpen(false);
                    load();
                },
                onError: () => notify(editing ? 'Error updating note' : 'Error adding note', 'error'),
            }
        );
    };

    const handleDelete = (id) => {
        runGuarded(
            (extra) => dataProvider.delete(`employees/${employmentId}/contract-notes`, { id, data: extra }),
            {
                onSuccess: () => { notify('Note deleted', 'success'); load(); },
                onError: () => notify('Error deleting note', 'error'),
            }
        );
    };

    return (
        <Box mb={3}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="h6">Contract Notes</Typography>
                <Button label="Add Note" onClick={openAdd}><AddIcon /></Button>
            </Box>
            <Typography variant="body2" color="textSecondary" gutterBottom>
                Filled into any template with a "contract_notes" tag when a contract is sent — all notes are combined into one block, separated by a blank line.
            </Typography>
            {loading ? <Typography variant="body2">Loading...</Typography> : (
                notes.length === 0
                    ? <Typography variant="body2" color="textSecondary">No contract notes added.</Typography>
                    : notes.map(n => (
                        <Box key={n.id} display="flex" alignItems="flex-start" justifyContent="space-between" py={1} borderBottom="1px solid #eee">
                            <Typography variant="body2" style={{ whiteSpace: 'pre-wrap', flex: 1 }}>{n.note}</Typography>
                            <Box flexShrink={0} ml={1}>
                                <IconButton size="small" onClick={() => openEdit(n)}><EditIcon fontSize="small" /></IconButton>
                                <IconButton size="small" onClick={() => handleDelete(n.id)}><DeleteIcon fontSize="small" /></IconButton>
                            </Box>
                        </Box>
                    ))
            )}

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{editing ? 'Edit Note' : 'Add Note'}</DialogTitle>
                <DialogContent>
                    <Box mt={1}>
                        <MuiTextField
                            label="Note"
                            value={noteText}
                            onChange={e => setNoteText(e.target.value)}
                            fullWidth
                            multiline
                            minRows={3}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button label="Cancel" onClick={() => setDialogOpen(false)} />
                    <Button label="Save" onClick={handleSave} disabled={!noteText.trim()} />
                </DialogActions>
            </Dialog>

            {guardDialog}
        </Box>
    );
};

// ---------------------------------------------------------------------------
// Deal Panel
// ---------------------------------------------------------------------------

const dealStatusLabels = {
    DRAFT: 'Draft',
    PENDING_APPROVAL: 'Pending approval',
    REJECTED: 'Rejected by an approver',
    AWAITING_ACCEPTANCE: 'Awaiting crew member',
    DECLINED: 'Declined by crew member',
    ACCEPTED: 'Accepted',
};

const dealEventLabels = {
    submitted: 'Sent for approval',
    withdrawn: 'Withdrawn',
    approved: 'Approved',
    rejected: 'Rejected',
    released: 'Sent to crew member',
    accepted: 'Accepted by crew member',
    declined: 'Declined by crew member',
    amended: 'Amended',
    contract_cancelled: 'Contract cancelled',
    contract_auto_send_failed: 'Automatic contract send failed',
};

const DEAL_SUBMITTABLE = ['DRAFT', 'REJECTED', 'DECLINED'];

const DealPanel = ({ employmentId, dealStatus }) => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();
    const [events, setEvents] = React.useState([]);
    const [busy, setBusy] = React.useState(false);

    const loadHistory = React.useCallback(() => {
        dataProvider.getList(`employees/${employmentId}/deal/history`, { pagination: {}, sort: {}, filter: {} })
            .then(({ data }) => setEvents(data))
            .catch(() => setEvents([]));
    }, [dataProvider, employmentId]);

    React.useEffect(() => { loadHistory(); }, [loadHistory, dealStatus]);

    const act = (path, successMessage) => {
        setBusy(true);
        dataProvider.create(`employees/${employmentId}/deal/${path}`, { data: {} })
            .then(() => {
                notify(successMessage, 'success');
                setBusy(false);
                refresh();
            })
            .catch(err => {
                notify(err?.body?.message ?? err?.message ?? 'Something went wrong', 'error');
                setBusy(false);
            });
    };

    return (
        <Box mb={3}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="h6">Deal</Typography>
            </Box>
            <Box mb={1}>
                <Chip
                    label={dealStatusLabels[dealStatus] ?? dealStatus}
                    size="small"
                    color={dealStatus === 'ACCEPTED' ? 'primary' : (dealStatus === 'REJECTED' || dealStatus === 'DECLINED') ? 'secondary' : 'default'}
                />
            </Box>
            <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                {DEAL_SUBMITTABLE.includes(dealStatus) && (
                    <Button label="Send for approval" onClick={() => act('submit', 'Deal sent')} disabled={busy} />
                )}
                {dealStatus === 'PENDING_APPROVAL' && (
                    <Button label="Withdraw" onClick={() => act('withdraw', 'Deal withdrawn')} disabled={busy} />
                )}
            </Box>
            {DEAL_SUBMITTABLE.includes(dealStatus) && (
                <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 4 }}>
                    Save any changes first, the deal is sent as it was last saved.
                </Typography>
            )}
            {dealStatus === 'PENDING_APPROVAL' && (
                <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 4 }}>
                    The approvers are reviewing this deal. Withdraw it to change its terms.
                </Typography>
            )}
            {events.length > 0 && (
                <Box mt={2}>
                    <Typography variant="subtitle2" gutterBottom>History</Typography>
                    {events.map(e => (
                        <Box key={e.id} py={0.5} borderBottom="1px solid #eee">
                            <Typography variant="body2">
                                {dealEventLabels[e.action] ?? e.action}
                                {e.user?.name ? ` — ${e.user.name}` : ''}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                {e.created_at}{e.note ? ` — ${e.note}` : ''}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};

// ---------------------------------------------------------------------------
// Contract Template Panel
// ---------------------------------------------------------------------------

const ContractTemplatePanel = ({ employmentId, productionId, contractTemplateId, contractTemplateOverridden }) => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const [templates, setTemplates] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [templateId, setTemplateId] = React.useState(contractTemplateId ?? '');
    const [overridden, setOverridden] = React.useState(!!contractTemplateOverridden);
    const [saving, setSaving] = React.useState(false);

    React.useEffect(() => {
        setLoading(true);
        dataProvider.getList('contract-templates', {
            filter: { production_id: productionId },
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'name', order: 'ASC' },
        })
            .then(({ data }) => { setTemplates(data); setLoading(false); })
            .catch(() => setLoading(false));
    }, [dataProvider, productionId]);

    // Keep local state in sync if the parent record refreshes after a save
    React.useEffect(() => {
        setTemplateId(contractTemplateId ?? '');
        setOverridden(!!contractTemplateOverridden);
    }, [contractTemplateId, contractTemplateOverridden]);

    const handleChange = (event) => {
        const raw = event.target.value;
        const payload = raw === '' ? { contract_template_id: null } : { contract_template_id: Number(raw) };

        setSaving(true);
        dataProvider.create(`employees/${employmentId}/contract-template`, { data: payload })
            .then(({ data }) => {
                setTemplateId(data.contract_template_id ?? '');
                setOverridden(!!data.contract_template_overridden);
                notify(raw === '' ? 'Reverted to auto-selected template' : 'Contract template updated', 'success');
                setSaving(false);
            })
            .catch(() => {
                notify('Error updating contract template', 'error');
                setSaving(false);
            });
    };

    const currentTemplate = templates.find(t => String(t.id) === String(templateId));

    return (
        <Box mb={3}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="h6">Contract Template</Typography>
            </Box>
            {loading ? <Typography variant="body2">Loading...</Typography> : (
                <>
                    <MuiTextField
                        select
                        label="Template"
                        value={templateId ?? ''}
                        onChange={handleChange}
                        fullWidth
                        disabled={saving}
                        InputLabelProps={{ shrink: true }}
                        SelectProps={{ native: true }}
                    >
                        <option value="">Auto-select (recommended)</option>
                        {templates.map(t => (
                            <option key={t.id} value={t.id}>{t.name}{t.is_default ? ' (default)' : ''}</option>
                        ))}
                    </MuiTextField>
                    <Box mt={1}>
                        {overridden ? (
                            <Chip label="Manually overridden" size="small" color="secondary" />
                        ) : (
                            <Chip label={`Auto-selected${currentTemplate ? `: ${currentTemplate.name}` : ''}`} size="small" />
                        )}
                    </Box>
                </>
            )}
        </Box>
    );
};

// ---------------------------------------------------------------------------
// Send Contract Panel
// ---------------------------------------------------------------------------

const contractStatusLabels = {
    DRAFT: 'Draft',
    SENT: 'Sent — awaiting signature',
    COMPLETED: 'Signed',
    DECLINED: 'Declined',
    VOID: 'Cancelled',
};

const BLOCKING_STATUSES = ['DRAFT', 'SENT', 'COMPLETED'];
const CANCELLABLE_STATUSES = ['DRAFT', 'SENT', 'COMPLETED'];

const SendContractPanel = ({ employmentId, dealStatus = null, dealAcceptedAt = null }) => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const [contracts, setContracts] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [sending, setSending] = React.useState(false);
    const [checkingSlots, setCheckingSlots] = React.useState(false);
    const [actionLoading, setActionLoading] = React.useState(false);

    const [cancelDialogOpen, setCancelDialogOpen] = React.useState(false);
    const [cancelReason, setCancelReason] = React.useState('');

    const [replaceDialogOpen, setReplaceDialogOpen] = React.useState(false);
    const [replaceName, setReplaceName] = React.useState('');
    const [replaceEmail, setReplaceEmail] = React.useState('');

    const [sendDialogOpen, setSendDialogOpen] = React.useState(false);
    const [recipientSlots, setRecipientSlots] = React.useState([]);
    const [recipientDetails, setRecipientDetails] = React.useState({});

    const currentRecipient = (contract) =>
        contract.recipients?.find(r => !['completed', 'rejected'].includes(r.status));

    const load = () => {
        setLoading(true);
        dataProvider.getList(`employments/${employmentId}/contracts`, { pagination: {}, sort: {}, filter: {} })
            .then(({ data }) => { setContracts(data); setLoading(false); })
            .catch(() => setLoading(false));
    };

    React.useEffect(() => { load(); }, [employmentId]);

    // Most recent contract first — assumes the endpoint returns them in
    // creation order like the rest of this app's lists do
    const latestContract = contracts[0];
    // On the deal flow a contract may only go out for an accepted deal. A
    // contract from an earlier version of the deal does not block sending, it
    // is voided once the new one has gone out. The server refuses a second
    // contract for the same version of the deal.
    const usesDeals = dealStatus !== null && dealStatus !== undefined;
    const dealAccepted = !usesDeals || dealStatus === 'ACCEPTED';
    // On the deal flow a live contract only blocks if it was created after the
    // current acceptance, i.e. it is for the current terms. One from an older
    // version does not block; it is voided once the replacement goes out.
    const acceptedAt = dealAcceptedAt ? new Date(dealAcceptedAt).getTime() : null;
    const blockingContract = contracts.find(c =>
        BLOCKING_STATUSES.includes(c.status) &&
        (!usesDeals || (acceptedAt !== null && new Date(c.created_at).getTime() >= acceptedAt - 1000))
    );
    const recipient = latestContract ? currentRecipient(latestContract) : null;
    const bounced = !!(
        latestContract &&
        recipient &&
        latestContract.last_bounce_recipient_key === recipient.key
    );

    const doSend = (recipients) => {
        setSending(true);
        dataProvider.create('contracts', {
            data: {
                employment_id: employmentId,
                ...(recipients && recipients.length ? { recipients } : {}),
            },
        })
            .then(() => {
                notify('Contract sent', 'success');
                setSending(false);
                setSendDialogOpen(false);
                load();
            })
            .catch(err => {
                notify(err?.body?.message ?? err?.message ?? 'Error sending contract', 'error');
                setSending(false);
            });
    };

    const handleSend = () => {
        setCheckingSlots(true);
        dataProvider.getList('contracts/send-recipient-slots', {
            filter: { employment_id: employmentId },
            pagination: {},
            sort: {},
        })
            .then(({ data }) => {
                console.log('SLOTS RESULT', data);   // ← temporary
                setCheckingSlots(false);
                if (!data || data.length === 0) {
                    doSend([]);
                    return;
                }
                setRecipientSlots(data);
                setRecipientDetails(
                    data.reduce((acc, slot) => ({ ...acc, [slot.key]: { name: '', email: '' } }), {})
                );
                setSendDialogOpen(true);
            })
            .catch(err => {
                setCheckingSlots(false);
                notify(err?.body?.message ?? err?.message ?? 'Error checking recipient requirements', 'error');
            });
    };

    const updateRecipientField = (key, field, value) => {
        setRecipientDetails(prev => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
    };

    const canSubmitSendDialog = recipientSlots.every(slot => {
        const v = recipientDetails[slot.key];
        return v && v.name?.trim() && v.email?.trim();
    });

    const handleSendDialogSubmit = () => {
        const recipients = recipientSlots.map(slot => ({
            key: slot.key,
            name: recipientDetails[slot.key]?.name ?? '',
            email: recipientDetails[slot.key]?.email ?? '',
        }));
        doSend(recipients);
    };

    const handleRemind = () => {
        if (!latestContract || !recipient) return;
        setActionLoading(true);
        dataProvider.create(`contracts/${latestContract.id}/resend`, {
            data: { recipient_key: recipient.key },
        })
            .then(() => {
                notify(`Reminder sent to ${recipient.name}`, 'success');
                setActionLoading(false);
            })
            .catch(err => {
                notify(err?.body?.message ?? err?.message ?? 'Error sending reminder', 'error');
                setActionLoading(false);
            });
    };

    const handleDownload = () => {
        dataProvider.getFile(`contracts/${latestContract.id}/download`)
            .then(downloadGetFileResult)
            .catch(() => notify('Error downloading signed contract', 'error'));
    };    

    const openCancelDialog = () => {
        setCancelReason('');
        setCancelDialogOpen(true);
    };

    const handleCancel = () => {
        if (!latestContract) return;
        setActionLoading(true);
        dataProvider.create(`contracts/${latestContract.id}/cancel`, {
            data: { reason: cancelReason || undefined },
        })
            .then(() => {
                notify('Contract cancelled', 'success');
                setActionLoading(false);
                setCancelDialogOpen(false);
                load();
            })
            .catch(err => {
                notify(err?.body?.message ?? err?.message ?? 'Error cancelling contract', 'error');
                setActionLoading(false);
            });
    };

    const openReplaceDialog = () => {
        setReplaceName(recipient?.name ?? '');
        setReplaceEmail('');
        setReplaceDialogOpen(true);
    };

    const handleReplace = () => {
        if (!latestContract || !recipient) return;
        setActionLoading(true);
        dataProvider.create(`contracts/${latestContract.id}/replace-signer`, {
            data: { recipient_key: recipient.key, name: replaceName, email: replaceEmail },
        })
            .then(() => {
                notify('Signer replaced', 'success');
                setActionLoading(false);
                setReplaceDialogOpen(false);
                load();
            })
            .catch(err => {
                notify(err?.body?.message ?? err?.message ?? 'Error replacing signer', 'error');
                setActionLoading(false);
            });
    };

    return (
        <Box mb={3}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="h6">Contract</Typography>
            </Box>
            {loading ? <Typography variant="body2">Loading...</Typography> : (
                <>
                    {latestContract ? (
                        <Box mb={1} display="flex" alignItems="center">
                            <Chip
                                label={
                                    latestContract.status === 'SENT' && recipient
                                        ? `Sent — awaiting ${recipient.name}`
                                        : (contractStatusLabels[latestContract.status] ?? latestContract.status)
                                }
                                size="small"
                                color={
                                    latestContract.status === 'COMPLETED' ? 'primary'
                                        : latestContract.status === 'DECLINED' ? 'secondary'
                                            : 'default'
                                }
                            />
                            {bounced && (
                                <Chip
                                    label={`Email ${latestContract.last_bounce_type} bounced`}
                                    size="small"
                                    color="secondary"
                                    style={{ marginLeft: 8 }}
                                />
                            )}
                        </Box>
                    ) : (
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            No contract sent yet.
                        </Typography>
                    )}

                    <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                        <Button
                            label="Send Contract"
                            onClick={handleSend}
                            disabled={sending || checkingSlots || !!blockingContract || !dealAccepted}
                        />
                        {latestContract && latestContract.status === 'SENT' && recipient && (
                            <Button label="Remind" onClick={handleRemind} disabled={actionLoading} />
                        )}
                        {latestContract && CANCELLABLE_STATUSES.includes(latestContract.status) && (
                            <Button
                                label={latestContract.status === 'COMPLETED' ? 'Void Contract' : 'Cancel Contract'}
                                onClick={openCancelDialog}
                                disabled={actionLoading}
                            />
                        )}
                        {latestContract && latestContract.status === 'SENT' && recipient && (
                            <Button label="Replace Signer" onClick={openReplaceDialog} disabled={actionLoading} />
                        )}
                        {latestContract && latestContract.signed_document_path && (
                            <Button label="Download Signed Contract" onClick={handleDownload}>
                                <GetAppIcon />
                            </Button>
                        )}
                    </Box>

                    {usesDeals && !dealAccepted && (
                        <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 4 }}>
                            The crew member needs to accept the deal before a contract can be sent.
                        </Typography>
                    )}

                    {blockingContract && (
                        <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 4 }}>
                            A contract is already {contractStatusLabels[blockingContract.status]?.toLowerCase()}. Cancel it first to send a new one.
                        </Typography>
                    )}
                </>
            )}

            <Dialog open={sendDialogOpen} onClose={() => setSendDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Recipient details needed</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                        This contract template needs details for the following recipients before it can be sent.
                    </Typography>
                    {recipientSlots.map(slot => (
                        <Box key={slot.key} mt={2}>
                            <Typography variant="subtitle2">{slot.label || slot.key}</Typography>
                            <Box mb={1} mt={1}>
                                <MuiTextField
                                    label="Name"
                                    value={recipientDetails[slot.key]?.name ?? ''}
                                    onChange={e => updateRecipientField(slot.key, 'name', e.target.value)}
                                    fullWidth
                                />
                            </Box>
                            <Box mb={1}>
                                <MuiTextField
                                    label="Email"
                                    type="email"
                                    value={recipientDetails[slot.key]?.email ?? ''}
                                    onChange={e => updateRecipientField(slot.key, 'email', e.target.value)}
                                    fullWidth
                                />
                            </Box>
                        </Box>
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button label="Cancel" onClick={() => setSendDialogOpen(false)} />
                    <Button
                        label="Send Contract"
                        onClick={handleSendDialogSubmit}
                        disabled={sending || !canSubmitSendDialog}
                    />
                </DialogActions>
            </Dialog>

            <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{latestContract?.status === 'COMPLETED' ? 'Void Signed Contract' : 'Cancel Contract'}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                        {latestContract?.status === 'COMPLETED'
                            ? 'This marks the signed contract as void on our side so a corrected one can be sent. The original signed document is not affected and remains available to download below.'
                            : 'This voids the envelope on SignatureAPI — the crew member will no longer be able to sign it.'}
                    </Typography>
                    <Box mt={1}>
                        <MuiTextField
                            label="Reason (optional)"
                            value={cancelReason}
                            onChange={e => setCancelReason(e.target.value)}
                            fullWidth
                            multiline
                            minRows={2}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button label="Back" onClick={() => setCancelDialogOpen(false)} />
                    <Button
                        label={latestContract?.status === 'COMPLETED' ? 'Void Contract' : 'Cancel Contract'}
                        onClick={handleCancel}
                        disabled={actionLoading}
                    />
                </DialogActions>
            </Dialog>

            <Dialog open={replaceDialogOpen} onClose={() => setReplaceDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Replace Signer{recipient ? ` — ${recipient.name}` : ''}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                        SignatureAPI will re-send the envelope to the new name/email for this recipient.
                    </Typography>
                    <Box mb={2} mt={1}>
                        <MuiTextField
                            label="Name"
                            value={replaceName}
                            onChange={e => setReplaceName(e.target.value)}
                            fullWidth
                        />
                    </Box>
                    <Box mb={2}>
                        <MuiTextField
                            label="Email"
                            type="email"
                            value={replaceEmail}
                            onChange={e => setReplaceEmail(e.target.value)}
                            fullWidth
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button label="Cancel" onClick={() => setReplaceDialogOpen(false)} />
                    <Button
                        label="Replace Signer"
                        onClick={handleReplace}
                        disabled={actionLoading || !replaceName.trim() || !replaceEmail.trim()}
                    />
                </DialogActions>
            </Dialog>
        </Box>
    );
};

// ---------------------------------------------------------------------------
// Misc components
// ---------------------------------------------------------------------------

const ResendInvitationButton = ({ record }) => {
    const notify = useNotify();
    const refresh = useRefresh();
    const dataProvider = useDataProvider();

    const resendInvitation = () => dataProvider.create(
        `employees/${record.id}/resend-invitation`,
        { data: {} }
    ).then(() => {
        notify('Invitation has been re-sent', 'success');
        refresh();
    });

    if (record.status === 'INVITE_SENT' || record.deal_status === 'AWAITING_ACCEPTANCE') {
        return <Button label="Re-send Invitation" onClick={resendInvitation} />;
    }

    return null;
};

const EmploymentStartFormDownloadButton = ({ record }) => {
    const dataProvider = useDataProvider();

    const downloadStartForm = () => dataProvider.getFile(
        `employees/${record.id}/start-form-pdf`
    ).then(downloadGetFileResult);

    if (!record.is_onboarded) {
        return '';
    }

    return <Button
        variant="contained"
        to={`employments/${record.id}/start-form-pdf`}
        label="Start Form"
        title="Download start form"
        onClick={downloadStartForm}
    >
        <GetAppIcon />
    </Button>;
};

const EmploymentTitle = props => {
    return <span>{props.record ? `${props.record.first_name} ${props.record.surname} - ${props.record.department.name}` : ''}</span>;
};

const SectionHeading = ({ title }) => (
    <Box mt={2} mb={1}>
        <Typography variant="h6" gutterBottom>{title}</Typography>
        <Divider />
    </Box>
);

const ReadOnlyField = ({ label, source, record }) => (
    <Box mb={1} mr={2}>
        <Typography variant="caption" color="textSecondary">{label}</Typography>
        <Typography variant="body2">{record?.[source] ?? '—'}</Typography>
    </Box>
);

const hireTypeChoices = [
    { id: 'local_hire', name: 'Local Hire' },
    { id: 'distant_hire', name: 'Distant Hire' },
];

const prodMeetingChoices = [
    { id: 'work', name: 'Work' },
    { id: 'designated_holiday', name: 'Designated Holiday' },
];

const readThroughChoices = [
    { id: 'work', name: 'Work' },
    { id: 'designated_holiday', name: 'Designated Holiday' },
];

const filters = [
    <TextInput label="Search" source="search" alwaysOn />,
    <ReferenceInput perPage={100} label="Department" source="department_id" reference="departments" emptyText="All" resettable={true} alwaysOn>
        <SelectInput optionText="name" />
    </ReferenceInput>,
    <ReferenceInput perPage={100} label="Payroll" source="payroll_id" reference="payrolls" emptyText="All" resettable={true} alwaysOn>
        <SelectInput optionText="name" />
    </ReferenceInput>,
];

export const EmploymentsList = props => (
    <List {...props} filters={filters} sort={{ field: 'surname', order: 'asc' }}>
        <Datagrid>
            <TextField source="first_name" label="First Name" />
            <TextField source="surname" label="Surname" />
            <TextField source="department.name" label="Department" />
            <TextField source="job_title" label="Job Title" />
            <TextField source="status" label="Status" />
            <FunctionField label="Deal" sortable={false} render={record => record.deal_status ? (dealStatusLabels[record.deal_status] ?? record.deal_status) : '-'} />
            <ResendInvitationButton />
            <EmploymentStartFormDownloadButton label="Start Form" />
            <EditButton />
            <DeleteButton />
        </Datagrid>
    </List>
);

const PeriodDateFields = ({ prefix, label, record = null }) => (
    <Box mb={2}>
        <Typography variant="subtitle2" gutterBottom>{label}</Typography>
        <DateInput source={`${prefix}_start_date`} label="Start Date" fullWidth />
        <DateInput source={`${prefix}_end_date`} label="End Date" fullWidth />
        {record && (
            <Box display="flex" mt={1}>
                <ReadOnlyField label="Total Days" source={`${prefix}_total_days`} record={record} />
                <ReadOnlyField label="Weeks" source={`${prefix}_weeks`} record={record} />
                <ReadOnlyField label="Days" source={`${prefix}_days`} record={record} />
            </Box>
        )}
    </Box>
);

// ---------------------------------------------------------------------------
// EmploymentsEdit
// ---------------------------------------------------------------------------

// Module-level state — survives component unmount/remount caused by
// FormWithRedirect resetting the Edit component on save failure.
let _warningState = { open: false, kind: 'job_title', jobTitle: '', contractMessage: '', notice: null, pendingId: null, pendingValues: null };
let _warningListeners = [];
const setWarningState = (state) => {
    _warningState = { ..._warningState, ...state };
    _warningListeners.forEach(fn => fn(_warningState));
};
const subscribeWarning = (fn) => {
    _warningListeners.push(fn);
    return () => { _warningListeners = _warningListeners.filter(f => f !== fn); };
};

export const JobTitleWarningOverlay = () => {
    const [state, setState] = React.useState(_warningState);

    React.useEffect(() => {
        return subscribeWarning(setState);
    }, []);

    const handleConfirm = React.useCallback(() => {
        const { pendingId, pendingValues, kind } = state;
        setWarningState({ open: false });
        // Re-trigger the update with confirmation — stored on window to avoid
        // circular dependency
        if (window._employmentDoUpdate) {
            window._employmentDoUpdate(
                pendingId,
                pendingValues,
                kind === 'contract' ? { confirm_contract_cancel: true } : true
            );
        }
    }, [state]);

    // The end date is being changed on an employment which has been given
    // notice. Say what to do with the notice and save again with that choice.
    const handleNotice = React.useCallback((action) => {
        const { pendingId, pendingValues } = state;
        setWarningState({ open: false });
        if (window._employmentDoUpdate) {
            window._employmentDoUpdate(pendingId, pendingValues, { notice_action: action });
        }
    }, [state]);

    const handleCancel = React.useCallback(() => {
        setWarningState({ open: false, pendingId: null, pendingValues: null });
    }, []);

    if (!state.open) return null;
    const isContract = state.kind === 'contract';
    const isNotice = state.kind === 'notice';

    if (isNotice) {
        const notice = state.notice ?? {};
        const canReissue = (notice.options ?? []).includes('reissue');

        return ReactDOM.createPortal(
            <ThemeProvider theme={createTheme()}>
                <Dialog open={state.open} onClose={handleCancel} maxWidth="sm" fullWidth>
                    <DialogTitle>This employment has been given notice</DialogTitle>
                    <DialogContent>
                        <Typography variant="body1" gutterBottom>
                            Notice was given on {formatDate(notice.notice_issued_at)} for an end date of{' '}
                            <strong>{formatDate(notice.notice_end_date)}</strong>.{' '}
                            {notice.new_end_date
                                ? <>You are changing the end date to <strong>{formatDate(notice.new_end_date)}</strong>.</>
                                : 'You are removing the end date.'}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            The notice would otherwise keep telling the crew member the old date. Choose what happens to it:
                        </Typography>
                        <Typography variant="body2" color="textSecondary" component="div">
                            <strong>Rescind</strong> emails the crew member to say the notice no longer applies.
                            {canReissue && <><br /><strong>Reissue</strong> emails them a revised notice with the new end date.</>}
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <MuiButton onClick={handleCancel}>Cancel — go back</MuiButton>
                        <MuiButton onClick={() => handleNotice('rescind')} color="primary" variant="contained">Rescind notice and save</MuiButton>
                        {canReissue && (
                            <MuiButton onClick={() => handleNotice('reissue')} color="primary" variant="contained">Reissue notice and save</MuiButton>
                        )}
                    </DialogActions>
                </Dialog>
            </ThemeProvider>,
            document.body
        );
    }

    return ReactDOM.createPortal(
        <ThemeProvider theme={createTheme()}>
            <Dialog open={state.open} onClose={handleCancel} maxWidth="sm" fullWidth>
                <DialogTitle>{isContract ? 'This will cancel the contract' : 'Job title not on HMRC approved list'}</DialogTitle>
                <DialogContent>
                    {isContract ? (
                        <>
                            <Typography variant="body1" gutterBottom>
                                {state.contractMessage}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                The deal will go back to draft. Send it for approval again, and once the
                                crew member has accepted it a new contract can be sent.
                            </Typography>
                        </>
                    ) : (
                        <>
                            <Typography variant="body1" gutterBottom>
                                The job title <strong>"{state.jobTitle}"</strong> does not appear on the HMRC
                                approved list for this contract type (Loan-out / Self-Employed).
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                If you proceed, this title will be saved for this production only. It will not
                                be added to the master HMRC list.
                            </Typography>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <MuiButton onClick={handleCancel}>Cancel — go back</MuiButton>
                    <MuiButton onClick={handleConfirm} color="primary" variant="contained">{isContract ? 'Cancel contract and save' : 'Confirm and proceed'}</MuiButton>
                </DialogActions>
            </Dialog>
        </ThemeProvider>,
        document.body
    );
};

export const EmploymentsEdit = props => {
    const notify = useNotify();
    const redirect = useRedirect();
    const dataProvider = useDataProvider();

    const transform = (data) => {
        const d = { ...data };
        delete d.production_id;
        delete d.department;
        // Returned by the server when a saved change needs follow up, never sent back
        delete d.warnings;
        return d;
    };

    const doUpdate = React.useCallback(
        // `confirm` is true to confirm the job title (as before), or an object
        // of confirmation flags to add, e.g. { confirm_contract_cancel: true }
        (id, values, confirm = false) => {
            const extra = confirm === true ? { confirm_job_title: true } : (confirm || {});
            const data = { ...values, ...extra };
            return dataProvider.update('employees', { id, data })
                .then(result => {
                    // The save has gone through. If the email was changed while a
                    // contract is out for signature, that contract is still
                    // addressed to the old email, so say so (it does not block)
                    const signerWarning = result?.data?.warnings?.contract_signer_email_outdated;

                    if (signerWarning) {
                        notify(`Employment updated. ${signerWarning.message}`, 'warning', {}, false, 20000);
                    } else {
                        notify('Employment updated', 'success');
                    }

                    redirect('/employees');
                })
                .catch(err => {
                    const warning = err?.body?.warnings?.job_title_not_approved;
                    if (warning) {
                        setWarningState({
                            open: true,
                            kind: 'job_title',
                            jobTitle: warning.job_title,
                            pendingId: id,
                            pendingValues: data,
                        });
                        return;
                    }
                    const contractWarning = err?.body?.warnings?.contract_will_be_cancelled;
                    if (contractWarning) {
                        setWarningState({
                            open: true,
                            kind: 'contract',
                            contractMessage: contractWarning.message,
                            pendingId: id,
                            pendingValues: data,
                        });
                        return;
                    }
                    const noticeWarning = err?.body?.warnings?.notice_will_be_outdated;
                    if (noticeWarning) {
                        setWarningState({
                            open: true,
                            kind: 'notice',
                            notice: noticeWarning,
                            pendingId: id,
                            pendingValues: data,
                        });
                        return;
                    }
                    // A plain message, e.g. the deal is pending approval and has to be withdrawn first
                    if (err?.body?.message && !err.body.errors && Object.keys(err.body).length <= 1) {
                        notify(err.body.message, 'error');
                        return;
                    }
                    if (typeof err.body === 'object') {
                        return err.body;
                    }
                    notify(err.message ?? 'Error saving employment', 'error');
                });
        },
        [dataProvider, notify, redirect],
    );

    // Store doUpdate on window so the overlay can call it after confirmation
    React.useEffect(() => {
        window._employmentDoUpdate = doUpdate;
        return () => { window._employmentDoUpdate = null; };
    }, [doUpdate]);

    const save = React.useCallback((values) => {
        const transformed = transform(values);
        doUpdate(transformed.id ?? values.id, transformed);
    }, [doUpdate]);

    return (
        <Edit {...props} title={<EmploymentTitle />} undoable={false}>
            <FormWithRedirect
                save={save}
                render={formProps => {
                    const record = formProps.record;
                    const rateType = record?.rate_type;
                    const hasAgent = record?.has_agent;

                    return (
                        <Box p="1em">
                            <Box display="flex">
                                <Box flex={1} mr="1em">
                                    <form>
                                        <SectionHeading title="Personal Details" />
                                        <TextInput source="first_name" fullWidth />
                                        <TextInput source="surname" fullWidth />
                                        <TextInput
                                            source="email"
                                            type="email"
                                            fullWidth
                                            validate={[required(), validateEmail()]}
                                            helperText={record?.employee_users?.length > 0
                                                ? 'Changing this replaces the crew member linked to this employment: the new address is sent an invitation and the previous one loses access. The deal and its approvals are not affected.'
                                                : undefined}
                                        />
                                        <TextInput source="phone" fullWidth />
                                        <ReferenceInput label="Department" source="department_id" reference="departments" fullWidth filter={{ production_id: record.production_id }} perPage={100}>
                                            <SelectInput optionText="name" optionValue="id" />
                                        </ReferenceInput>
                                        <ReferenceInput label="Payroll" source="payroll_id" reference="payrolls" fullWidth filter={{ production_id: record.production_id }} perPage={100} allowEmpty>
                                            <SelectInput optionText="name" optionValue="id" />
                                        </ReferenceInput>
                                        <TextInput source="payroll_number" label="Payroll Number" fullWidth />
                                        <TextInput source="job_title" fullWidth />

                                        <SectionHeading title="Contract Details" />
                                        <SelectInput source="contract_type" choices={[
                                            { id: 'daily', name: 'Daily' },
                                            { id: 'contract', name: 'Contract' },
                                        ]} fullWidth />
                                        <SelectInput source="hire_type" choices={hireTypeChoices} fullWidth allowEmpty />
                                        <SelectInput source="employment_type" label="Employment Type" choices={[
                                            { id: 'paye', name: 'PAYE' },
                                            { id: 'ltdCo', name: 'Loan-out' },
                                            { id: 'soleTrader', name: 'Self-Employed' },
                                        ]} fullWidth allowEmpty />
                                        <DateInput source="employment_start_date" fullWidth />
                                        <DateInput source="employment_end_date" fullWidth />
                                        <DateInput source="access_end_date" fullWidth helperText="Defaults to one week after the employment end date if not provided" />
                                        <NoticePeriodInput />
                                        <TextInput source="start_time" fullWidth />
                                        <TextInput source="end_time" fullWidth />
                                        <SelectInput source="prod_meeting" label="Prod Meeting" choices={prodMeetingChoices} fullWidth allowEmpty />
                                        <SelectInput source="read_through" label="Read Through" choices={readThroughChoices} fullWidth allowEmpty />
                                        <Box display="flex" mt={1}>
                                            <ReadOnlyField label="Total Days" source="total_days" record={record} />
                                            <ReadOnlyField label="Total Weeks" source="total_weeks" record={record} />
                                            <ReadOnlyField label="Remaining Days" source="total_days_left" record={record} />
                                        </Box>

                                        <SectionHeading title="Rates" />
                                        <SelectInput source="rate_type" choices={[
                                            { id: 'daily', name: 'Daily' },
                                            { id: 'hourly', name: 'Hourly' },
                                        ]} fullWidth />
                                        <NumberInput source="days_per_week" label="Days Per Week" fullWidth helperText="e.g. 5" />
                                        <NumberInput source="daily_basic_hours" label="Daily Basic Hours" fullWidth helperText="e.g. 10" />
                                        {rateType === 'daily' && (
                                            <NumberInput source="daily_rate_gross" label="Daily Rate (Gross)" fullWidth />
                                        )}
                                        {rateType === 'hourly' && (
                                            <NumberInput source="hourly_rate_gross" label="Hourly Rate (Gross)" fullWidth />
                                        )}
                                        <Box mt={2}>
                                            <Typography variant="subtitle2" gutterBottom>Calculated Rates</Typography>
                                            <Box display="flex" flexWrap="wrap">
                                                <ReadOnlyField label="Daily Rate (Net)" source="daily_rate_net" record={record} />
                                                <ReadOnlyField label="Daily Rate (Holiday)" source="daily_rate_holiday" record={record} />
                                                <ReadOnlyField label="Hourly Rate (Gross)" source="hourly_rate_gross" record={record} />
                                                <ReadOnlyField label="Hourly Rate (Net)" source="hourly_rate_net" record={record} />
                                                <ReadOnlyField label="Hourly Rate (Holiday)" source="hourly_rate_hol" record={record} />
                                                <ReadOnlyField label="Weekly Rate (Gross)" source="weekly_rate_gross" record={record} />
                                                <ReadOnlyField label="Weekly Rate (Net)" source="weekly_rate_net" record={record} />
                                                <ReadOnlyField label="Weekly Rate (Holiday)" source="weekly_rate_hol" record={record} />
                                                <ReadOnlyField label="Weekly ACH (Gross)" source="weekly_gross_ach" record={record} />
                                                <ReadOnlyField label="Weekly ACH (Net)" source="weekly_net_ach" record={record} />
                                                <ReadOnlyField label="Weekly ACH (Holiday)" source="weekly_hol_ach" record={record} />
                                            </Box>
                                        </Box>

                                        <SectionHeading title="Allowances" />
                                        <BooleanInput source="per_diems" label="Per Diems" />

                                        <SectionHeading title="Period Dates" />
                                        <PeriodDateFields prefix="pre_prep" label="Pre-Prep" record={record} />
                                        <PeriodDateFields prefix="prep" label="Prep" record={record} />
                                        <PeriodDateFields prefix="shoot" label="Shoot" record={record} />
                                        <PeriodDateFields prefix="wrap" label="Wrap" record={record} />

                                        <SectionHeading title="Agent" />
                                        <BooleanInput source="has_agent" label="Has Agent" />
                                        {hasAgent && (
                                            <>
                                                <TextInput source="agent_name" fullWidth />
                                                <TextInput source="agent_email" fullWidth />
                                                <TextInput source="agent_address" fullWidth multiline />
                                            </>
                                        )}

                                        <SectionHeading title="Production Flags" />
                                        <BooleanInput source="is_shoot_crew" label="Shoot Crew" />
                                        <BooleanInput source="is_hod" label="Head of Department" />
                                        <BooleanInput source="is_trainee" label="Trainee" />
                                        <BooleanInput source="is_vat_registered" label="VAT Registered" />
                                        <BooleanInput source="ach" label="Additional Contracted Hour (ACH)" />
                                        <BooleanInput source="is_onboarded" label="Is Onboarded?" />

                                        <Box display="flex" justifyContent="space-between" width="100%">
                                            <Toolbar disableGutters={true}>
                                                <SaveButton
                                                    saving={formProps.saving}
                                                    handleSubmitWithRedirect={formProps.handleSubmitWithRedirect}
                                                />
                                                <ResendInvitationButton record={record} />
                                            </Toolbar>
                                        </Box>
                                    </form>
                                </Box>

                                <Box flex={2} ml="1em">
                                    {record.deal_status && (
                                        <DealPanel employmentId={record.id} dealStatus={record.deal_status} />
                                    )}

                                    <EmploymentAllowancesPanel
                                        employmentId={record.id}
                                        productionId={record.production_id}
                                    />

                                    <PenaltyRatesPanel employmentId={record.id} />

                                    <EmploymentContractNotesPanel employmentId={record.id} />

                                    <ContractTemplatePanel
                                        employmentId={record.id}
                                        productionId={record.production_id}
                                        contractTemplateId={record.contract_template_id}
                                        contractTemplateOverridden={record.contract_template_overridden}
                                    />

                                    <SendContractPanel employmentId={record.id} dealStatus={record.deal_status ?? null} dealAcceptedAt={record.deal_accepted_at ?? null} />

                                    <NoticePanel
                                        employmentId={record.id}
                                        endDate={record.employment_end_date}
                                        noticePeriodDays={record.notice_period_days}
                                        employmentStatus={record.status}
                                    />

                                    <HiatusesPanel
                                        resource={`employees/${record.id}/hiatuses`}
                                        label="Hiatuses"
                                    />

                                    <Typography variant="h6" gutterBottom>Users</Typography>
                                    <ResourceContextProvider value="employment-users">
                                        <EmploymentUsersList id={record.id} />
                                    </ResourceContextProvider>

                                    <AddEmploymentUsers employmentId={record.id} />

                                    <Typography variant="h6" gutterBottom>Update Requests</Typography>
                                    <ResourceContextProvider value="employment-update-requests">
                                        <EmploymentUpdateRequestList id={record.id} />
                                    </ResourceContextProvider>

                                    <PenaltyGroupAssignmentPanel
                                        type="employment"
                                        id={record.id}
                                        productionId={record.production_id}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    );
                }}
            />
        </Edit>
    );
};

// ---------------------------------------------------------------------------
// CreateEmployment
// ---------------------------------------------------------------------------

const TabPanel = (props) => {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    <Typography>{children}</Typography>
                </Box>
            )}
        </div>
    );
};

export const CreateEmployment = (props) => {
    const [value, setValue] = React.useState(0);
    const [rateType, setRateType] = React.useState(null);
    const [hasAgent, setHasAgent] = React.useState(false);
    const [warningOpen, setWarningOpen] = React.useState(false);
    const [warningJobTitle, setWarningJobTitle] = React.useState('');
    const pendingValuesRef = React.useRef(null);
    const redirect = useRedirect();
    const notify = useNotify();

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    const [mutate] = useMutation();

    // Performs the actual create call, with an optional confirm flag
    const doCreate = React.useCallback(
        (values, confirm = false) => {
            const data = confirm ? { ...values, confirm_job_title: true } : values;
            return mutate({
                type: 'create',
                resource: 'employees',
                payload: { data },
            }, { returnPromise: true })
                .then(() => {
                    notify('ra.notification.created', 'success');
                    redirect('/employees');
                })
                .catch(error => {
                    const warning = error?.body?.warnings?.job_title_not_approved;
                    if (warning) {
                        pendingValuesRef.current = values;
                        setWarningJobTitle(warning.job_title);
                        setWarningOpen(true);
                        return;
                    }
                    if (typeof error.body === 'object') {
                        return error.body;
                    }
                    throw error;
                });
        },
        [mutate, notify, redirect],
    );

    const save = React.useCallback(
        (values) => doCreate(values),
        [doCreate],
    );

    const handleConfirm = () => {
        setWarningOpen(false);
        doCreate(pendingValuesRef.current, true);
    };

    const handleCancel = () => {
        setWarningOpen(false);
        pendingValuesRef.current = null;
    };

    return (
        <>
            <Dialog open={warningOpen} onClose={handleCancel} maxWidth="sm" fullWidth>
                <DialogTitle>Job title not on HMRC approved list</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        The job title <strong>"{warningJobTitle}"</strong> does not appear on the HMRC approved
                        list for this contract type (Loan-out / Self-Employed).
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        If you proceed, this title will be saved for this production only. It will not
                        be added to the master HMRC list.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button label="Cancel — go back" onClick={handleCancel} />
                    <Button label="Confirm and proceed" onClick={handleConfirm} />
                </DialogActions>
            </Dialog>
            <Box sx={{ width: '100%' }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
                        <Tab label="Bulk Import" />
                        <Tab label="Manual Entry" />
                    </Tabs>
                </Box>
                <TabPanel value={value} index={0}>
                    <ProductionEmploymentsImportForm {...props} />
                </TabPanel>
                <TabPanel value={value} index={1}>
                    <Create {...props} redirect="list" title="Create Employment">
                        <SimpleForm save={save}>
                            <SectionHeading title="Personal Details" />
                            <TextInput source="first_name" fullWidth />
                            <TextInput source="surname" fullWidth />
                            <TextInput source="email" fullWidth />
                            <TextInput source="phone" fullWidth />
                            <ReferenceInput label="Department" source="department_id" reference="departments" perPage={100} fullWidth>
                                <SelectInput optionText="name" />
                            </ReferenceInput>
                            <ReferenceInput label="Payroll" source="payroll_id" reference="payrolls" perPage={100} allowEmpty fullWidth>
                                <SelectInput optionText="name" optionValue="id" />
                            </ReferenceInput>
                            <TextInput source="payroll_number" label="Payroll Number" fullWidth />
                            <TextInput source="job_title" fullWidth />

                            <SectionHeading title="Contract Details" />
                            <SelectInput source="contract_type" choices={[
                                { id: 'daily', name: 'Daily' },
                                { id: 'contract', name: 'Contract' },
                            ]} fullWidth />
                            <SelectInput source="hire_type" choices={hireTypeChoices} fullWidth allowEmpty />
                            <SelectInput source="employment_type" label="Employment Type" choices={[
                                { id: 'paye', name: 'PAYE' },
                                { id: 'ltdCo', name: 'Loan-out' },
                                { id: 'soleTrader', name: 'Self-Employed' },
                            ]} fullWidth allowEmpty />
                            <DateInput source="employment_start_date" fullWidth />
                            <DateInput source="employment_end_date" fullWidth />
                            <NoticePeriodInput defaultValue={7} />
                            <TextInput source="start_time" fullWidth />
                            <TextInput source="end_time" fullWidth />
                            <SelectInput source="prod_meeting" label="Prod Meeting" choices={prodMeetingChoices} fullWidth allowEmpty />
                            <SelectInput source="read_through" label="Read Through" choices={readThroughChoices} fullWidth allowEmpty />

                            <SectionHeading title="Rates" />
                            <SelectInput source="rate_type" choices={[
                                { id: 'daily', name: 'Daily' },
                                { id: 'hourly', name: 'Hourly' },
                            ]} fullWidth onChange={e => setRateType(e.target.value)} />
                            <NumberInput source="days_per_week" label="Days Per Week" fullWidth helperText="e.g. 5" />
                            <NumberInput source="daily_basic_hours" label="Daily Basic Hours" fullWidth helperText="e.g. 10" />
                            {rateType === 'daily' && (
                                <NumberInput source="daily_rate_gross" label="Daily Rate (Gross)" fullWidth />
                            )}
                            {rateType === 'hourly' && (
                                <NumberInput source="hourly_rate_gross" label="Hourly Rate (Gross)" fullWidth />
                            )}

                            <SectionHeading title="Allowances" />
                            <BooleanInput source="per_diems" label="Per Diems" defaultValue={false} />

                            <SectionHeading title="Period Dates" />
                            <PeriodDateFields prefix="pre_prep" label="Pre-Prep" />
                            <PeriodDateFields prefix="prep" label="Prep" />
                            <PeriodDateFields prefix="shoot" label="Shoot" />
                            <PeriodDateFields prefix="wrap" label="Wrap" />

                            <SectionHeading title="Agent" />
                            <BooleanInput source="has_agent" label="Has Agent" onChange={e => setHasAgent(e.target.checked)} />
                            {hasAgent && (
                                <>
                                    <TextInput source="agent_name" fullWidth />
                                    <TextInput source="agent_email" fullWidth />
                                    <TextInput source="agent_address" fullWidth multiline />
                                </>
                            )}

                            <SectionHeading title="Production Flags" />
                            <BooleanInput source="is_shoot_crew" label="Shoot Crew" />
                            <BooleanInput source="is_hod" label="Head of Department" />
                            <BooleanInput source="is_trainee" label="Trainee" />
                            <BooleanInput source="is_vat_registered" label="VAT Registered" />
                            <BooleanInput source="ach" label="Additional Contracted Hour (ACH)" />
                            <BooleanInput source="is_onboarded" label="Is Onboarded?" />
                        </SimpleForm>
                    </Create>
                </TabPanel>
            </Box>
        </>
    );
};
